import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Baby, LogOut, Plus, FileText, Calendar, Newspaper, Edit2, Trash2, KeyRound, DollarSign, Landmark, Send, Download } from 'lucide-react';
import FileUpload from '../components/FileUpload';
import StudentAvatar from '../components/StudentAvatar';
import FeedbackSection from '../components/FeedbackSection';
import NewsSection from '../components/NewsSection';
import BirthdaySection from '../components/BirthdaySection';
import NotificationBell from '../components/NotificationBell';
import CalendarEventsSection from '../components/CalendarEventsSection';
import useBirthdayGreeting from '../hooks/useBirthdayGreeting';
import CnsPyramid from '../components/CnsPyramid';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMalaysiaTimeOnly = (value) => {
  if (!value) return '—';
  if (/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) return value;
  try {
    return new Date(value).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
  } catch (e) {
    return value;
  }
};

const emptyStudent = {
  name: '', id_number: '', date_of_birth: '', address: '', parents_name: '', parents_phone: '', parents_date_of_birth: '',
  other_school: '', emergency_contact_name: '', emergency_contact_phone: '',
  vehicle_registration: '', vehicle_brand: '', special_needs_notes: '',
  allergies: '', health_info: '',
  location: '',
  oku_card: 'no',
  student_photo_id: '', parents_photo_id: '',
};

const statusBadge = (status) => {
  const map = {
    pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    acknowledged: 'bg-blue-100 text-blue-700 border-blue-300',
    approved: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    cancelled: 'bg-slate-100 text-slate-700 border-slate-300',
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    present: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    absent: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    late: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    leave: 'bg-blue-100 text-blue-700 border-blue-300',
  };
  return <Badge className={`${map[status] || 'bg-slate-100 text-slate-700 border-slate-300'} border`}>{status}</Badge>;
};

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  useBirthdayGreeting(user);
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [reports, setReports] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [news, setNews] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [studentDialog, setStudentDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentForm, setStudentForm] = useState(emptyStudent);
  const [leaveForm, setLeaveForm] = useState({ student_id: '', start_date: '', end_date: '', reason: '' });
  const [reportView, setReportView] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [companyInfo, setCompanyInfo] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [declareOpen, setDeclareOpen] = useState(false);
  const [declaringPayment, setDeclaringPayment] = useState(null);
  const [declareForm, setDeclareForm] = useState({ paid_date: new Date().toISOString().slice(0, 10), payment_method: 'FPX', payment_reference: '', note: '' });
  const [parentFeedbackForm, setParentFeedbackForm] = useState({ feedback: '', rating: 0 });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/api/students`, { withCredentials: true }),
        axios.get(`${API}/api/reports`, { withCredentials: true }),
        axios.get(`${API}/api/student-leaves`, { withCredentials: true }),
        axios.get(`${API}/api/news`, { withCredentials: true }),
        axios.get(`${API}/api/payments`, { withCredentials: true }),
        axios.get(`${API}/api/company-info`, { withCredentials: true }),
      ]);
      const [s, r, l, n, p, c] = results;
      const studentsData = s.status === 'fulfilled' ? s.value.data : [];
      setStudents(studentsData);
      setReports(r.status === 'fulfilled' ? r.value.data : []);
      setLeaves(l.status === 'fulfilled' ? l.value.data : []);
      setNews(n.status === 'fulfilled' ? n.value.data : []);
      setPayments(p.status === 'fulfilled' ? p.value.data : []);
      if (c.status === 'fulfilled') {
        setCompanyInfo(c.value.data && Object.keys(c.value.data).length ? c.value.data : null);
      }
      setSelectedStudentId((prev) => prev || (studentsData.length > 0 ? studentsData[0].id : ''));
      const failed = results.filter((res) => res.status === 'rejected');
      if (failed.length > 0) {
        console.error('Some parent dashboard data failed to load', failed);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttendance = useCallback(async () => {
    if (!selectedStudentId) {
      setAttendance([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/attendance`, {
        params: { student_id: selectedStudentId, month: currentMonth },
        withCredentials: true,
      });
      setAttendance(data);
    } catch (err) {
      console.error('Failed to fetch attendance', err);
      setAttendance([]);
    }
  }, [selectedStudentId, currentMonth]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  useEffect(() => {
    if (!companyInfo?.bank_qr_storage_path) {
      setQrPreviewUrl(null);
      return;
    }

    let active = true;
    axios.get(`${API}/api/company-info/qr`, { responseType: 'blob', withCredentials: true })
      .then((resp) => {
        if (!active) return;
        const url = URL.createObjectURL(resp.data);
        setQrPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch(() => {
        if (active) setQrPreviewUrl(null);
      });

    return () => {
      active = false;
      setQrPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [companyInfo?.bank_qr_storage_path]);

  const openDeclarePayment = (p) => {
    setDeclaringPayment(p);
    setDeclareForm({ paid_date: new Date().toISOString().slice(0, 10), payment_method: 'FPX', payment_reference: '', note: '' });
    setDeclareOpen(true);
  };

  const submitDeclaration = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/payments/${declaringPayment.id}/declare`, declareForm, { withCredentials: true });
      toast.success('Payment declared. Awaiting admin verification.');
      setDeclareOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to declare payment');
    }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await axios.get(`${API}/api/payments/${p.id}/receipt-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${p.student_name.replace(/\s+/g, '_')}_${p.month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Receipt not available yet');
    }
  };

  const submitParentReportFeedback = async () => {
    if (!parentFeedbackForm.feedback.trim()) {
      toast.error('Please write feedback before submitting');
      return;
    }
    try {
      const payload = {
        feedback: parentFeedbackForm.feedback.trim(),
        rating: parentFeedbackForm.rating || null,
      };
      const { data } = await axios.post(`${API}/api/reports/${reportView.id}/parent-feedback`, payload, { withCredentials: true });
      toast.success('Feedback submitted. Thank you!');
      setFeedbackSubmitted(true);
      setReportView(data);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const openCreateStudent = () => { setEditingStudent(null); setStudentForm(emptyStudent); setStudentDialog(true); };
  const openEditStudent = (s) => {
    setEditingStudent(s);
    setStudentForm({ ...emptyStudent, ...s });
    setStudentDialog(true);
  };

  const requiredStudentFields = [
    'name', 'id_number', 'date_of_birth', 'address', 'parents_name', 'parents_phone', 'parents_date_of_birth',
    'other_school', 'emergency_contact_name', 'emergency_contact_phone',
    'vehicle_registration', 'vehicle_brand', 'location', 'oku_card'
  ];

  const studentFieldLabels = {
    name: 'Student Name',
    id_number: 'ID Number',
    date_of_birth: 'Date of Birth',
    address: 'Address',
    parents_name: 'Parent Name',
    parents_phone: 'Parent Phone',
    parents_date_of_birth: 'Parent Birthday',
    other_school: 'School Name',
    emergency_contact_name: 'Emergency Contact Name',
    emergency_contact_phone: 'Emergency Contact Phone',
    vehicle_registration: 'Vehicle Registration',
    vehicle_brand: 'Vehicle Brand / Type',
    location: 'Location',
    oku_card: 'OKU Card',
  };

  const getIncompleteStudentFields = () => {
    return requiredStudentFields.filter((field) => {
      const value = studentForm[field];
      return !value || value.toString().trim() === '';
    });
  };

  const saveStudent = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await axios.put(`${API}/api/students/${editingStudent.id}`, studentForm, { withCredentials: true });
        toast.success('Student updated');
      } else {
        await axios.post(`${API}/api/students`, studentForm, { withCredentials: true });
        toast.success('Student added');
      }
      setStudentDialog(false);
      fetchAll();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to save student';
      console.error('Student save failed', err);
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    }
  };

  const isStudentFormComplete = () => {
    return getIncompleteStudentFields().length === 0;
  };

  const deleteStudent = async (id) => {
    if (!window.confirm('Delete this student profile?')) return;
    try {
      await axios.delete(`${API}/api/students/${id}`, { withCredentials: true });
      toast.success('Student deleted');
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/student-leaves`, leaveForm, { withCredentials: true });
      toast.success('Leave request submitted');
      setLeaveForm({ student_id: leaveForm.student_id, start_date: '', end_date: '', reason: '' });
      fetchAll();
    } catch (e) { toast.error('Failed to submit leave'); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('Minimum 6 characters'); return; }
    try {
      await axios.post(`${API}/api/auth/change-password`,
        { current_password: passwordForm.current_password, new_password: passwordForm.new_password },
        { withCredentials: true });
      toast.success('Password changed');
      setChangePasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    }
  };

  const selectedStudent = students.find((s) => s.id === selectedStudentId);
  const monthReports = reports.filter((r) => selectedStudentId ? r.student_id === selectedStudentId : true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="parent-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_teach-track-48/artifacts/r4w4bz0k_IMG_20260701_210749.png" alt="SSTC Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>SSTC e-Management System</h1>
              <p className="text-xs text-slate-500">Parent Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900" data-testid="parent-name">{user?.name}</p>
              <p className="text-xs text-slate-500">Parent</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setChangePasswordOpen(true)} data-testid="parent-change-password-button"><KeyRound className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="parent-logout-button"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Student selector for viewing reports/attendance */}
        {students.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm font-medium">Viewing:</Label>
            <div className="flex flex-wrap gap-2">
              {students.map((s) => (
                <Button
                  key={s.id}
                  variant={selectedStudentId === s.id ? 'default' : 'outline'}
                  size="sm"
                  className={selectedStudentId === s.id ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => setSelectedStudentId(s.id)}
                  data-testid={`student-selector-${s.id}`}
                >
                  {s.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        <Tabs defaultValue="news" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto" data-testid="parent-tabs">
            <TabsTrigger value="news" data-testid="tab-news">News & Announcements</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">My Children</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Monthly Reports</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves">Leave Requests</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          </TabsList>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card className="bg-white border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Children</CardTitle>
                  <CardDescription>Manage your children's profiles</CardDescription>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreateStudent} data-testid="add-student-button">
                  <Plus className="w-4 h-4 mr-2" /> Add Child
                </Button>
              </CardHeader>
              <CardContent>
                {students.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Baby className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No children added yet — click "Add Child" to start</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="students-list">
                    {students.map((s, idx) => (
                      <div key={s.id} className="p-5 border border-slate-200 rounded-lg" data-testid={`student-card-${idx}`}>
                        <div className="flex items-start gap-4 mb-3">
                          <StudentAvatar photoId={s.student_photo_id} name={s.name} size="lg" testId={`student-avatar-${idx}`} />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-900 text-lg">{s.name}</h3>
                                <p className="text-sm text-slate-500">ID: {s.id_number || 'N/A'}</p>
                                {s.package_name && <p className="text-xs text-blue-600 mt-1">Package: {s.package_name}</p>}
                              </div>
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" onClick={() => openEditStudent(s)} data-testid={`edit-student-${idx}`}><Edit2 className="w-4 h-4" /></Button>
                                <Button variant="outline" size="sm" onClick={() => deleteStudent(s.id)} className="text-red-600" data-testid={`delete-student-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <p><span className="font-medium">Other School:</span> {s.other_school || '—'}</p>
                          <p><span className="font-medium">Emergency:</span> {s.emergency_contact_name || '—'}</p>
                          <p className="col-span-2"><span className="font-medium">Vehicle:</span> {s.vehicle_brand || '—'} ({s.vehicle_registration || '—'})</p>
                          {s.allergies && <p className="col-span-2 text-red-600"><span className="font-medium">Allergies:</span> {s.allergies}</p>}
                          {s.health_info && <p className="col-span-2"><span className="font-medium">Health:</span> {s.health_info}</p>}
                          {s.special_needs_notes && <p className="col-span-2 mt-1 text-slate-700"><span className="font-medium">Notes:</span> {s.special_needs_notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Progress Reports</CardTitle>
                <CardDescription>Approved reports from your child's teachers</CardDescription>
              </CardHeader>
              <CardContent>
                {monthReports.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No reports available yet</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="reports-list">
                    {monthReports.map((r, idx) => (
                      <div key={r.id} className="p-4 border border-slate-200 rounded-lg cursor-pointer hover:shadow-sm" onClick={() => setReportView(r)} data-testid={`report-item-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-900">{r.student_name} — {r.month}</h4>
                            <p className="text-xs text-slate-500">By {r.teacher_name} • Rating: {r.overall_rating || '—'}/5</p>
                          </div>
                          {statusBadge(r.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Attendance</CardTitle>
                    <CardDescription>
                      {selectedStudent ? `${selectedStudent.name} — Package: ${selectedStudent.monthly_attendance_days || 20} days/month` : 'Select a child above'}
                    </CardDescription>
                  </div>
                  <Input type="month" value={currentMonth} onChange={(e) => setCurrentMonth(e.target.value)} className="w-40" data-testid="attendance-month-input" />
                </div>
              </CardHeader>
              <CardContent>
                {!selectedStudentId ? (
                  <div className="text-center py-8 text-slate-500">Select a child to view attendance</div>
                ) : attendance.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No attendance records for this month</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-xs text-emerald-600 font-semibold uppercase">Present</p>
                        <p className="text-2xl font-semibold">{attendance.filter(a => a.status === 'present').length}</p>
                      </div>
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-600 font-semibold uppercase">Absent</p>
                        <p className="text-2xl font-semibold">{attendance.filter(a => a.status === 'absent').length}</p>
                      </div>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-600 font-semibold uppercase">Late</p>
                        <p className="text-2xl font-semibold">{attendance.filter(a => a.status === 'late').length}</p>
                      </div>
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-600 font-semibold uppercase">On Leave</p>
                        <p className="text-2xl font-semibold">{attendance.filter(a => a.status === 'leave').length}</p>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Time In</TableHead>
                          <TableHead>Time Out</TableHead>
                          <TableHead>Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="attendance-table">
                        {attendance.map((a, idx) => (
                          <TableRow key={a.id} data-testid={`attendance-row-${idx}`}>
                            <TableCell>{a.date}</TableCell>
                            <TableCell>{statusBadge(a.status)}</TableCell>
                            <TableCell>{formatMalaysiaTimeOnly(a.time_in)}</TableCell>
                            <TableCell>{formatMalaysiaTimeOnly(a.time_out)}</TableCell>
                            <TableCell className="text-sm text-slate-600">{a.note || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaves Tab */}
          <TabsContent value="leaves">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave Requests</CardTitle>
                  <CardDescription>Leave requests submitted for your children</CardDescription>
                </CardHeader>
                <CardContent>
                  {leaves.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">No leave requests</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Child</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="student-leaves-table">
                        {leaves.map((l, idx) => (
                          <TableRow key={l.id} data-testid={`student-leave-row-${idx}`}>
                            <TableCell className="font-medium">{l.student_name}</TableCell>
                            <TableCell className="text-sm">{l.start_date} to {l.end_date}</TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-xs truncate">{l.reason}</TableCell>
                            <TableCell>{statusBadge(l.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Submit Leave</CardTitle>
                  <CardDescription>Request leave for your child</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submitLeave} className="space-y-3" data-testid="submit-student-leave-form">
                    <div>
                      <Label>Child</Label>
                      <select
                        className="w-full mt-1 rounded-md border border-slate-300 h-10 px-3 text-sm"
                        value={leaveForm.student_id}
                        onChange={(e) => setLeaveForm({ ...leaveForm, student_id: e.target.value })}
                        required
                        data-testid="student-leave-child-select"
                      >
                        <option value="">Select child</option>
                        {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Start Date</Label>
                      <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} required data-testid="student-leave-start-input" />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} required data-testid="student-leave-end-input" />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} required placeholder="Reason for leave..." data-testid="student-leave-reason-input" />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-student-leave-button">Submit Leave</Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="space-y-6">
              <NewsSection isAdmin={false} />
              <BirthdaySection />
              <CalendarEventsSection isAdmin={false} />
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Payments</CardTitle>
                  <CardDescription>Payment status per month • Due end of each month</CardDescription>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                      <p>No payment records yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Paid Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="parent-payments-table">
                        {payments.map((p, idx) => (
                          <TableRow key={p.id} data-testid={`payment-row-${idx}`}>
                            <TableCell className="font-medium">{p.student_name}</TableCell>
                            <TableCell>{p.month}</TableCell>
                            <TableCell className="text-right">RM {p.amount?.toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-slate-600">{p.paid_date || '—'}</TableCell>
                            <TableCell>
                              {p.verification_status === 'verified' && <Badge className="bg-[#D1FAE5] text-[#10B981] border-[#10B981] border">Verified</Badge>}
                              {p.verification_status === 'received' && <Badge className="bg-blue-100 text-blue-700 border-blue-300 border">Received</Badge>}
                              {p.verification_status === 'pending_verification' && <Badge className="bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B] border">Awaiting Verification</Badge>}
                              {p.verification_status === 'rejected' && <Badge className="bg-[#FEE2E2] text-[#EF4444] border-[#EF4444] border">Rejected</Badge>}
                              {!p.verification_status && (
                                <Badge className={
                                  p.status === 'paid' ? 'bg-[#D1FAE5] text-[#10B981] border-[#10B981] border' :
                                  p.status === 'overdue' ? 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444] border' :
                                  'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B] border'
                                }>{p.status}</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {(p.status !== 'paid' && p.verification_status !== 'verified') && (
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => openDeclarePayment(p)} data-testid={`declare-payment-${idx}`}>
                                  I've Paid
                                </Button>
                              )}
                              {p.verification_status === 'verified' && (
                                <Button size="sm" variant="outline" onClick={() => downloadReceipt(p)} data-testid={`download-receipt-${idx}`}>
                                  <Download className="w-4 h-4 mr-1" /> Receipt
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Bank details card */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Landmark className="w-5 h-5 text-blue-600" /> Payment Instructions
                  </CardTitle>
                  <CardDescription>Transfer via FPX / online banking to:</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm" data-testid="parent-bank-info">
                  {companyInfo?.bank_name ? (
                    <>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Bank</p>
                        <p className="text-slate-900 font-medium">{companyInfo.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Account Number</p>
                        <p className="text-slate-900 font-mono font-semibold text-base">{companyInfo.bank_account_number}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Account Holder</p>
                        <p className="text-slate-900">{companyInfo.bank_account_holder}</p>
                      </div>
                      {companyInfo.bank_swift_code && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">SWIFT</p>
                          <p className="text-slate-900 font-mono">{companyInfo.bank_swift_code}</p>
                        </div>
                      )}
                      {qrPreviewUrl && (
                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Scan to Pay</p>
                          <img src={qrPreviewUrl} alt="Bank QR Code" className="mt-2 max-w-xs rounded-lg border border-slate-200" data-testid="parent-bank-qr" />
                        </div>
                      )}
                      <div className="text-xs text-slate-500 border-t pt-2 mt-3">
                        After transferring, click <span className="font-medium">"I've Paid"</span> on the payment row to notify admin for verification.
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-500 text-sm">Bank details not yet configured. Please contact admin for payment instructions.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Student Dialog */}
      <Dialog open={studentDialog} onOpenChange={setStudentDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="student-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingStudent ? 'Edit Child' : 'Add Child'}</DialogTitle>
            <DialogDescription>Enter your child's information (all fields are required)</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveStudent} className="space-y-4" data-testid="student-form">
            {!isStudentFormComplete() && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="incomplete-form-error">
                <p className="text-sm text-red-700 font-medium">⚠ Please fill in all required fields to continue</p>
                <ul className="mt-2 list-disc list-inside text-sm text-red-700">
                  {getIncompleteStudentFields().map((field) => (
                    <li key={field}>{studentFieldLabels[field] || field}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Student Photo</Label>
                <FileUpload testIdPrefix="student-photo" currentFileId={studentForm.student_photo_id} onFileUploaded={(f) => setStudentForm({ ...studentForm, student_photo_id: f ? f.id : '' })} />
              </div>
              <div>
                <Label>Parent Photo</Label>
                <FileUpload testIdPrefix="parent-photo" currentFileId={studentForm.parents_photo_id} onFileUploaded={(f) => setStudentForm({ ...studentForm, parents_photo_id: f ? f.id : '' })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} required data-testid="student-name-input" /></div>
              <div><Label>ID Number *</Label><Input value={studentForm.id_number} onChange={(e) => setStudentForm({ ...studentForm, id_number: e.target.value })} required data-testid="student-id-input" /></div>
              <div><Label>Date of Birth *</Label><Input type="date" value={studentForm.date_of_birth} onChange={(e) => setStudentForm({ ...studentForm, date_of_birth: e.target.value })} required data-testid="student-dob-input" /></div>
              <div><Label>Other School Name *</Label><Input value={studentForm.other_school} onChange={(e) => setStudentForm({ ...studentForm, other_school: e.target.value })} required data-testid="student-other-school-input" /></div>
              <div>
                <Label>Location *</Label>
                <Select value={studentForm.location} onValueChange={(v) => setStudentForm({ ...studentForm, location: v })}>
                  <SelectTrigger data-testid="student-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Penang">Penang</SelectItem>
                    <SelectItem value="Perak">Perak</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Parents Name *</Label><Input value={studentForm.parents_name} onChange={(e) => setStudentForm({ ...studentForm, parents_name: e.target.value })} required data-testid="student-parents-name-input" /></div>
              <div><Label>Parents Phone *</Label><Input value={studentForm.parents_phone} onChange={(e) => setStudentForm({ ...studentForm, parents_phone: e.target.value })} required data-testid="student-parents-phone-input" /></div>
              <div><Label>Parent Birthday *</Label><Input type="date" value={studentForm.parents_date_of_birth} onChange={(e) => setStudentForm({ ...studentForm, parents_date_of_birth: e.target.value })} required data-testid="student-parents-dob-input" /></div>
              <div><Label>Emergency Contact Name *</Label><Input value={studentForm.emergency_contact_name} onChange={(e) => setStudentForm({ ...studentForm, emergency_contact_name: e.target.value })} required data-testid="student-emergency-name-input" /></div>
              <div><Label>Emergency Contact Phone *</Label><Input value={studentForm.emergency_contact_phone} onChange={(e) => setStudentForm({ ...studentForm, emergency_contact_phone: e.target.value })} required data-testid="student-emergency-phone-input" /></div>
              <div><Label>Vehicle Registration *</Label><Input value={studentForm.vehicle_registration} onChange={(e) => setStudentForm({ ...studentForm, vehicle_registration: e.target.value })} placeholder="ABC 1234" required data-testid="student-vehicle-reg-input" /></div>
              <div><Label>Vehicle Brand / Type *</Label><Input value={studentForm.vehicle_brand} onChange={(e) => setStudentForm({ ...studentForm, vehicle_brand: e.target.value })} placeholder="Honda Civic" required data-testid="student-vehicle-brand-input" /></div>
              <div>
                <Label>OKU Card *</Label>
                <Select value={studentForm.oku_card} onValueChange={(v) => setStudentForm({ ...studentForm, oku_card: v })}>
                  <SelectTrigger data-testid="student-oku-select">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Address *</Label><Textarea value={studentForm.address} onChange={(e) => setStudentForm({ ...studentForm, address: e.target.value })} rows={2} required data-testid="student-address-input" /></div>
            <div><Label>Allergies</Label><Textarea value={studentForm.allergies} onChange={(e) => setStudentForm({ ...studentForm, allergies: e.target.value })} placeholder="Peanuts, dairy, etc..." rows={2} data-testid="student-allergies-input" /></div>
            <div><Label>Health Information</Label><Textarea value={studentForm.health_info} onChange={(e) => setStudentForm({ ...studentForm, health_info: e.target.value })} placeholder="Medical conditions, medications, blood type..." rows={2} data-testid="student-health-input" /></div>
            <div><Label>Special Needs Notes</Label><Textarea value={studentForm.special_needs_notes} onChange={(e) => setStudentForm({ ...studentForm, special_needs_notes: e.target.value })} placeholder="Any special conditions, therapies, medications..." rows={3} data-testid="student-notes-input" /></div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setStudentDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={!isStudentFormComplete()} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-student-button">{editingStudent ? 'Update' : 'Add Child'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Report View Dialog */}
      <Dialog open={!!reportView} onOpenChange={(o) => {
        if (!o) {
          // Prevent closing if parent hasn't submitted feedback on approved report
          if (reportView && reportView.status === 'approved' && !reportView.parent_feedback && !feedbackSubmitted) {
            toast.error('Please leave your feedback before closing this report.');
            return;
          }
          setReportView(null);
          setFeedbackSubmitted(false);
          setParentFeedbackForm({ feedback: '', rating: 0 });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="report-view-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Report — {reportView?.month}</DialogTitle>
            <DialogDescription>{reportView?.student_name} • By {reportView?.teacher_name}</DialogDescription>
          </DialogHeader>
          {reportView && (
            <div className="space-y-4">
              {reportView.overall_rating && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-semibold uppercase">Overall Progress Rating</p>
                  <p className="text-2xl font-semibold text-slate-900">{reportView.overall_rating}/5</p>
                </div>
              )}
              {[
                { label: "Highlights / Teacher's Feedback", key: 'highlights' },
                { label: 'Special Occasion / Event / Activity of the Month', key: 'special_event' },
                { label: 'Behaviour Observation', key: 'therapy_notes' },
                { label: 'Areas Needing Improvement', key: 'areas_of_improvement' },
                { label: 'Recommendations for Parents', key: 'recommendations' },
              ].filter((f) => reportView[f.key]).map((f) => (
                <div key={f.key}>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">{f.label}</p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{reportView[f.key]}</p>
                </div>
              ))}

              {/* CNS Pyramid */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Pyramid of Learning</p>
                <p className="text-xs text-slate-500 mb-2">Overall rating is calculated from the average of the POL ratings.</p>
                <div className="text-xs text-slate-500 mb-2">
                  <p><strong>Level 1</strong> Emerging – Requires extensive support</p>
                  <p><strong>Level 2</strong> Developing – Requires frequent support</p>
                  <p><strong>Level 3</strong> Progressing – Demonstrates skill with some support</p>
                  <p><strong>Level 4</strong> Proficient – Demonstrates skill independently</p>
                  <p><strong>Level 5</strong> Advanced – Consistently demonstrates skill beyond expectations</p>
                </div>
                <CnsPyramid ratings={reportView.cns_ratings || {}} editable={false} />
              </div>

              {reportView.status === 'approved' && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${API}/api/reports/${reportView.id}/pdf`, '_blank')}
                    data-testid="parent-download-report-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />Download PDF
                  </Button>
                </div>
              )}

              {/* Parent feedback section */}
              {reportView.status === 'approved' && (
                reportView.parent_feedback ? (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="parent-feedback-existing">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-1">Your Feedback (submitted)</p>
                    <p className="text-sm text-slate-700 whitespace-pre-line">{reportView.parent_feedback}</p>
                    {reportView.parent_feedback_rating && (
                      <p className="text-xs text-slate-500 mt-2">Rating: {reportView.parent_feedback_rating}/5 stars</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">Submitted {reportView.parent_feedback_submitted_at ? new Date(reportView.parent_feedback_submitted_at).toLocaleString() : ''}</p>
                  </div>
                ) : (
                  <div className="mt-4 p-4 border-2 border-blue-300 bg-blue-50 rounded-lg" data-testid="parent-feedback-required">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 mb-2">⚠ Your feedback is required</p>
                    <p className="text-sm text-slate-700 mb-3">Please share your thoughts on this month's progress before closing.</p>
                    <div className="mb-3">
                      <Label className="text-xs">Rating (optional)</Label>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setParentFeedbackForm({ ...parentFeedbackForm, rating: n })}
                            className={`w-8 h-8 rounded-full border ${parentFeedbackForm.rating >= n ? 'bg-amber-400 border-amber-500 text-white' : 'bg-white border-slate-300 text-slate-400'} font-semibold text-sm`}
                            data-testid={`parent-rating-${n}`}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>
                    <Textarea
                      placeholder="Share your thoughts, questions, or appreciation..."
                      value={parentFeedbackForm.feedback}
                      onChange={(e) => setParentFeedbackForm({ ...parentFeedbackForm, feedback: e.target.value })}
                      rows={4}
                      required
                      data-testid="parent-report-feedback-input"
                    />
                    <div className="flex justify-end gap-2 mt-3">
                      <Button
                        onClick={submitParentReportFeedback}
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={!parentFeedbackForm.feedback.trim()}
                        data-testid="submit-parent-report-feedback"
                      >
                        Submit Feedback
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Declare Payment Dialog */}
      <Dialog open={declareOpen} onOpenChange={setDeclareOpen}>
        <DialogContent className="max-w-md" data-testid="declare-payment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Declare Payment Made</DialogTitle>
            <DialogDescription>
              {declaringPayment && (
                <>For <strong>{declaringPayment.student_name}</strong> — {declaringPayment.month} — RM {declaringPayment.amount?.toFixed(2)}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDeclaration} className="space-y-3" data-testid="declare-payment-form">
            <div><Label>Paid Date *</Label><Input type="date" value={declareForm.paid_date} onChange={(e) => setDeclareForm({ ...declareForm, paid_date: e.target.value })} required data-testid="declare-paid-date-input" /></div>
            <div>
              <Label>Payment Method</Label>
              <select
                className="w-full mt-1 rounded-md border border-slate-300 h-10 px-3 text-sm"
                value={declareForm.payment_method}
                onChange={(e) => setDeclareForm({ ...declareForm, payment_method: e.target.value })}
                data-testid="declare-method-select"
              >
                <option value="FPX">FPX (online banking)</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div><Label>Transaction Reference / Receipt No.</Label><Input value={declareForm.payment_reference} onChange={(e) => setDeclareForm({ ...declareForm, payment_reference: e.target.value })} placeholder="e.g., FPX20260215123456" data-testid="declare-ref-input" /></div>
            <div><Label>Note (optional)</Label><Textarea value={declareForm.note} onChange={(e) => setDeclareForm({ ...declareForm, note: e.target.value })} rows={2} data-testid="declare-note-input" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDeclareOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-declare-payment-button">
                <Send className="w-4 h-4 mr-2" /> Submit Declaration
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-md" data-testid="parent-change-password-dialog">
          <DialogHeader><DialogTitle>Change Password</DialogTitle><DialogDescription>Enter your current password and choose a new one</DialogDescription></DialogHeader>
          <form onSubmit={changePassword} className="space-y-3">
            <div><Label>Current</Label><Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required data-testid="parent-current-password-input" /></div>
            <div><Label>New (min 6 chars)</Label><Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required minLength={6} data-testid="parent-new-password-input" /></div>
            <div><Label>Confirm</Label><Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required minLength={6} data-testid="parent-confirm-password-input" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t"><Button type="button" variant="outline" onClick={() => setChangePasswordOpen(false)}>Cancel</Button><Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="parent-save-password-button">Change</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentDashboard;
